import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation, Trans } from 'react-i18next';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import React from 'react';
import { MoreInfo } from '@/sections/extendedWarranty/MoreInfo';
import { HowItWorks } from '@/sections/extendedWarranty/HowItWorks';
import { ItPaysOff } from '@/sections/extendedWarranty/ItPaysOff';
import { DoYouKnowMore } from '@/sections/extendedWarranty/DoYouKnowMore';
import { LINKS } from '@/constants/links';
import { FaqIntro } from '@/sections/FaqIntro';
import { Benefits } from '@/sections/extendedWarranty/Benefits';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { Link } from '@/components/links';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { FILES } from '@/constants/files';
import Overlay from '@/sections/deprecatedInsurance/Overlay';
import { useRouter } from 'next/router';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(t0) {
  const $ = _c(64);
  const {
    pageBanner,
    menuPromoBanners
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["extendedWarranty", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  usePageView("extended-warranty");
  const {
    locale
  } = useRouter();
  let t2;
  if ($[1] !== t) {
    t2 = t("notAvailable.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("notAvailable.text");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== locale) {
    t4 = FILES(locale);
    $[5] = locale;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4.insuranceTerms) {
    t5 = {
      text: t3,
      href: t4.insuranceTerms
    };
    $[7] = t3;
    $[8] = t4.insuranceTerms;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <Overlay title={t2} link={t5} />;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("seo.title");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("seo.description");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <SeoHeader title={t7} description={t8} nofollow={true} noindex={true} />;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = pageBanner?.videoId;
  let t11;
  if ($[20] !== pageBanner?.title || $[21] !== t) {
    t11 = pageBanner?.title ?? t("banner.title");
    $[20] = pageBanner?.title;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== pageBanner?.text || $[24] !== t) {
    t12 = pageBanner?.text ?? t("banner.text");
    $[23] = pageBanner?.text;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== pageBanner || $[27] !== t) {
    t13 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().insurance,
      testId: "extendedWarrantyPrimaryCTA",
      analyticsParam: "extendedWarrantyPrimaryCTA",
      variant: "primary"
    }];
    $[26] = pageBanner;
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = pageBanner?.image?.url ?? "/images/sections/extendedWarranty/hero/hero.png";
  let t15;
  if ($[29] !== pageBanner?.image?.alt || $[30] !== t) {
    t15 = pageBanner?.image?.alt ?? t("banner.title");
    $[29] = pageBanner?.image?.alt;
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = {
      url: t14,
      alt: t15
    };
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = pageBanner?.variant || "mascots";
  let t18;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t16 || $[40] !== t17) {
    t18 = <Hero videoId={t10} title={t11} text={t12} buttons={t13} image={t16} variant={t17} />;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="absolute top-[31rem] right-0 h-64 w-64 overflow-hidden lg:top-1/3 lg:h-125 lg:w-125 lg:-translate-y-3/4"><div className="bgCircle absolute top-0 right-0 translate-x-1/4 -rotate-90" /></div>;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="relative z-30 bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t20}<div className="relative z-10 flex flex-col gap-20 lg:gap-40"><ItPaysOff /><HowItWorks /><Benefits /><DoYouKnowMore /></div></div>;
    t22 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[44] = t21;
    $[45] = t22;
  } else {
    t21 = $[44];
    t22 = $[45];
  }
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = LINKS();
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Link to="/caste-dotazy/pojisteni-nakupu" analyticsParam="faqInsurance" testId="faqInsurance" />;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t) {
    t25 = <FaqIntro link={t23.faq} text={<Trans i18nKey="faq.text" t={t}>{t24}</Trans>} />;
    $[48] = t;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <MoreInfo />;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t25) {
    t27 = <div className="relative z-10 flex flex-col gap-20 lg:gap-40">{t25}{t26}</div>;
    $[51] = t25;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t27) {
    t29 = <div className="relative overflow-hidden bg-blue-50 py-10 lg:py-20">{t27}{t28}</div>;
    $[54] = t27;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t18 || $[57] !== t29) {
    t30 = <div className="relative">{t18}{t19}{t21}{t22}{t29}</div>;
    $[56] = t18;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== menuPromoBanners || $[60] !== t30 || $[61] !== t6 || $[62] !== t9) {
    t31 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t9}{t30}</Layout>;
    $[59] = menuPromoBanners;
    $[60] = t30;
    $[61] = t6;
    $[62] = t9;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  return t31;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('prodlouzena-zaruka');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('extended-warranty'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'extendedWarranty', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;